// PlansSidebar.tsx — collapsible tree sidebar for Plans mode
// Categories: Overall Building Plan, Floor Plans, Roof Plans, Foundation Plan,
//             Wall Sections, Footing Sections, Door Types, Window Types

import { useState, useCallback } from 'react';
import type { PlansData } from '../types/panelvar';

interface Props {
  plans: PlansData | null;
  activeTab: string;
  onSelectTab: (tab: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface SectionDef {
  key: string;
  label: string;
  children?: { key: string; label: string }[];
}

function buildSections(plans: PlansData | null): SectionDef[] {
  const sections: SectionDef[] = [
    { key: 'overall', label: 'Overall Building Plan' },
  ];

  // Floor Plans
  const floorChildren = (plans?.floorPlans ?? []).map((fp, i) => ({
    key: `floor-${fp.id}`,
    label: fp.name || `Floor Plan ${i + 1}`,
  }));
  sections.push({ key: 'floors', label: 'Floor Plans', children: floorChildren });

  // Roof Plans
  const roofChildren = (plans?.roofPlans ?? []).map((rp, i) => ({
    key: `roof-${rp.id}`,
    label: rp.name || `Roof Plan ${i + 1}`,
  }));
  sections.push({ key: 'roofs', label: 'Roof Plans', children: roofChildren });

  sections.push({ key: 'foundation', label: 'Foundation Plan' });

  // Wall Sections
  const wallChildren = (plans?.wallSections ?? []).map((ws) => ({
    key: `wall-${ws.id}`,
    label: `Section ${ws.label}`,
  }));
  sections.push({ key: 'walls', label: 'Wall Sections', children: wallChildren });

  // Footing Sections
  const footChildren = (plans?.footingSections ?? []).map((fs) => ({
    key: `footing-${fs.id}`,
    label: `Footing ${fs.label}`,
  }));
  sections.push({ key: 'footings', label: 'Footing Sections', children: footChildren });

  // Door Types
  const doorChildren = (plans?.doorTypes ?? []).map((dt) => ({
    key: `door-${dt.id}`,
    label: dt.label || 'Door Type',
  }));
  sections.push({ key: 'doors', label: 'Door Types', children: doorChildren });

  // Window Types
  const winChildren = (plans?.windowTypes ?? []).map((wt) => ({
    key: `window-${wt.id}`,
    label: wt.label || 'Window Type',
  }));
  sections.push({ key: 'windows', label: 'Window Types', children: winChildren });

  return sections;
}

export default function PlansSidebar({ plans, activeTab, onSelectTab, collapsed, onToggleCollapse }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['floors', 'roofs', 'walls', 'footings', 'doors', 'windows']));
  const sections = buildSections(plans);

  const toggleExpand = useCallback((key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  if (collapsed) {
    return (
      <div className="building-sidebar collapsed">
        <button className="collapse-toggle" onClick={onToggleCollapse} title="Expand sidebar">▶</button>
      </div>
    );
  }

  return (
    <div className="building-sidebar">
      <div className="building-sidebar-header">
        <span className="building-sidebar-title">Building</span>
        {onToggleCollapse && (
          <button className="collapse-toggle" onClick={onToggleCollapse} title="Collapse sidebar">◀</button>
        )}
      </div>
      <div className="building-sidebar-tree">
        {sections.map(sec => (
          <div key={sec.key} className="building-tree-node">
            {sec.children ? (
              <>
                <div
                  className={`building-tree-parent${activeTab === sec.key ? ' active' : ''}`}
                  onClick={() => toggleExpand(sec.key)}
                >
                  <span className="building-tree-arrow">{expanded.has(sec.key) ? '▾' : '▸'}</span>
                  <span className="building-tree-label">{sec.label}</span>
                  <span className="building-tree-count">{sec.children.length}</span>
                </div>
                {expanded.has(sec.key) && sec.children.map(child => (
                  <div
                    key={child.key}
                    className={`building-tree-leaf${activeTab === child.key ? ' active' : ''}`}
                    onClick={() => onSelectTab(child.key)}
                  >
                    {child.label}
                  </div>
                ))}
              </>
            ) : (
              <div
                className={`building-tree-leaf top-level${activeTab === sec.key ? ' active' : ''}`}
                onClick={() => onSelectTab(sec.key)}
              >
                {sec.label}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
